import { useState } from "react";
import axios from "axios";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, SERVICES, TID } from "@/config/site";
import { Phone, Mail, MapPin, Loader2, CheckCircle2, AlertCircle, Send } from "lucide-react";
import ShareLocationButton from "@/components/site/ShareLocationButton";
import { useReveal } from "@/hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    subject: "",
    message: "",
  });
  const [state, setState] = useState({ loading: false, ok: false, error: "" });
  const leftRef = useReveal();
  const rightRef = useReveal(0.05);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: false, error: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setState({ loading: false, ok: true, error: "" });
      setForm({ name: "", email: "", phone: "", service: "", subject: "", message: "" });
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let msg = "Something went wrong. Please call us directly.";
      if (typeof raw === "string") msg = raw;
      else if (Array.isArray(raw) && raw.length > 0 && raw[0]?.msg) msg = raw.map((e) => e.msg).join(", ");
      else if (err?.response?.status === 422) msg = "Please check your entries and try again.";
      setState({ loading: false, ok: false, error: msg });
    }
  };

  return (
    <section id="contact" className="relative bg-[#0A0A0A] py-24 sm:py-32 border-t border-white/5 overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{ background: "radial-gradient(ellipse at 15% 0%, rgba(250,204,21,0.10) 0%, rgba(0,0,0,0) 55%)" }}
      />
      <div className="pointer-events-none absolute inset-0 bm-grain" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-12">
        <div ref={leftRef} className="lux-reveal lg:col-span-5">
          <div className="font-display uppercase tracking-[0.32em] text-yellow-400 text-xs flex items-center gap-2">
            <span className="inline-block h-1.5 w-6 bg-yellow-400 rounded-full" />
            Get Help
          </div>
          <h2 className="mt-3 font-display font-black uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            Emergency? <span className="text-yellow-400">Call.</span>
            <span className="block">Not urgent? Send a message.</span>
          </h2>
          <p className="mt-5 text-zinc-400 text-base sm:text-lg font-light leading-relaxed">
            For anything time-sensitive — a breakdown, an accident, a lockout — please call. For quotes, general questions and non-urgent jobs, the form works great.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="contact-call-link"
              aria-label={`Call BM Towing & Recovery 24/7 at ${PHONE_DISPLAY}`}
              className="flex items-center gap-4 bg-yellow-400 hover:bg-yellow-300 text-black rounded-2xl p-5 group lux-glow-yellow transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-yellow-300"
            >
              <div className="h-14 w-14 shrink-0 rounded-xl bg-black/20 flex items-center justify-center">
                <Phone className="h-7 w-7 text-black" strokeWidth={3} />
              </div>
              <div>
                <div className="font-display uppercase tracking-[0.22em] text-xs opacity-80">Call 24/7</div>
                <div className="font-display font-black text-2xl leading-tight">{PHONE_DISPLAY}</div>
              </div>
            </a>

            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email-link"
              className="flex items-center gap-4 lux-glass hover:border-yellow-400/50 rounded-2xl text-white p-5 transition-colors"
            >
              <div className="h-14 w-14 shrink-0 rounded-xl bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
                <Mail className="h-6 w-6 text-yellow-400" strokeWidth={2} />
              </div>
              <div>
                <div className="font-display uppercase tracking-[0.22em] text-[10px] text-zinc-400">Email</div>
                <div className="font-display font-bold text-base break-all">{EMAIL}</div>
              </div>
            </a>

            <div className="flex items-center gap-4 lux-glass rounded-2xl text-white p-5">
              <div className="h-14 w-14 shrink-0 rounded-xl bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-yellow-400" strokeWidth={2} />
              </div>
              <div>
                <div className="font-display uppercase tracking-[0.22em] text-[10px] text-zinc-400">Service Base</div>
                <div className="font-display font-bold text-base leading-snug">Kirkland, WA — serving the Eastside &amp; Greater Seattle</div>
              </div>
            </div>

            <ShareLocationButton
              testId={TID.contactShareLocationBtn}
              variant="luxury"
              label="Share My GPS Location"
              className="w-full text-base"
            />
            <p className="text-xs text-zinc-500 leading-relaxed font-light">
              Stranded? Tap the button — we'll get your GPS coordinates, a Google Maps link, and one-tap options to call, text, or send it to dispatch.
            </p>
          </div>
        </div>

        <div ref={rightRef} className="lux-reveal lux-reveal-delay-2 lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid={TID.contactForm}
            className="lux-glass-strong rounded-2xl p-6 sm:p-8 relative overflow-hidden"
            noValidate
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-32 -right-32 w-64 h-64 rounded-full opacity-30"
              style={{ background: "radial-gradient(circle, rgba(250,204,21,0.35) 0%, rgba(0,0,0,0) 70%)" }}
            />
            <div className="relative font-display uppercase tracking-[0.28em] text-yellow-400 text-[11px] flex items-center gap-2">
              <span className="inline-block h-1.5 w-6 bg-yellow-400 rounded-full" />
              Non-Urgent Request
            </div>
            <div className="relative mt-2 font-display font-black uppercase text-white text-2xl sm:text-3xl leading-tight tracking-tight">
              Send us the details.
            </div>

            <div className="relative mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full Name" required>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={setField("name")}
                  data-testid={TID.contactName}
                  className="bm-input"
                  placeholder="Jane Doe"
                />
              </Field>
              <Field label="Phone" required>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={setField("phone")}
                  data-testid={TID.contactPhone}
                  className="bm-input"
                  placeholder="(425) 555-0123"
                />
              </Field>
              <Field label="Email" required>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={setField("email")}
                  data-testid={TID.contactEmail}
                  className="bm-input"
                  placeholder="you@example.com"
                />
              </Field>
              <Field label="Service Needed">
                <select
                  value={form.service}
                  onChange={setField("service")}
                  data-testid={TID.contactService}
                  className="bm-input"
                >
                  <option value="">Select a service…</option>
                  {SERVICES.map((s) => (
                    <option key={s.slug} value={s.title}>
                      {s.title}
                    </option>
                  ))}
                  <option value="Other">Other / Not sure</option>
                </select>
              </Field>
            </div>

            <div className="relative mt-4">
              <Field label="Subject">
                <input
                  type="text"
                  value={form.subject}
                  onChange={setField("subject")}
                  data-testid={TID.contactSubject}
                  className="bm-input"
                  placeholder="e.g., Flatbed tow from Bellevue to Redmond"
                />
              </Field>
            </div>

            <div className="relative mt-4">
              <Field label="Message" required>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={setField("message")}
                  data-testid={TID.contactMessage}
                  className="bm-input resize-y"
                  placeholder="Vehicle year/make/model, pickup location, drop-off, and anything else useful."
                />
              </Field>
            </div>

            <div className="relative mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <button
                type="submit"
                disabled={state.loading}
                data-testid={TID.contactSubmit}
                className="inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-300 disabled:opacity-70 text-black font-display font-black uppercase tracking-wider px-7 py-4 text-base rounded-full lux-glow-yellow transition-colors min-h-[56px]"
              >
                {state.loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" strokeWidth={2.5} /> Send Request
                  </>
                )}
              </button>
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="contact-inline-call-btn"
                className="inline-flex items-center justify-center gap-2 text-yellow-400 hover:text-yellow-300 font-display uppercase tracking-[0.22em] text-xs transition-colors"
              >
                <Phone className="h-4 w-4" strokeWidth={2.5} /> Or call {PHONE_DISPLAY}
              </a>
            </div>

            {state.ok && (
              <div
                data-testid={TID.contactSuccess}
                role="status"
                aria-live="polite"
                className="relative mt-5 flex items-start gap-3 border border-green-500/40 bg-green-500/10 text-green-200 p-4 rounded-xl"
              >
                <CheckCircle2 className="h-5 w-5 mt-0.5" />
                <div>
                  <div className="font-display font-black uppercase">Message Sent</div>
                  <div className="text-sm mt-1 font-light">
                    Thanks — we got your request. For anything urgent, please call {PHONE_DISPLAY}.
                  </div>
                </div>
              </div>
            )}
            {state.error && (
              <div
                data-testid={TID.contactError}
                role="alert"
                aria-live="assertive"
                className="relative mt-5 flex items-start gap-3 border border-red-500/40 bg-red-500/10 text-red-200 p-4 rounded-xl"
              >
                <AlertCircle className="h-5 w-5 mt-0.5" />
                <div>
                  <div className="font-display font-black uppercase">Couldn't Send</div>
                  <div className="text-sm mt-1 font-light">{state.error}</div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>

      <style>{`
        .bm-input {
          width: 100%;
          background: rgba(0, 0, 0, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 10px;
          color: #f1f5f9;
          padding: 12px 14px;
          font-size: 15px;
          font-family: inherit;
          outline: none;
          transition: border-color 0.2s;
        }
        .bm-input:focus {
          border-color: rgba(250, 204, 21, 0.7);
        }
        .bm-input::placeholder { color: #64748b; }
      `}</style>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-display uppercase tracking-[0.18em] text-[10px] text-zinc-400">
        {label} {required && <span className="text-yellow-400">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
